import { useCallback, useMemo, useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  Building2,
  Calendar,
  ShieldAlert,
  UserRound,
  Users,
} from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import { bulletsToAlertCopy, bulletsToHotspots } from "@/utils/mistralInsightMappers";
import {
  buildRetentionAlertCopy,
  buildRetentionComboChart,
  buildRetentionInsightsMarkdown,
  buildRetentionRiskKpis,
  buildRiskHotspots,
  buildTransitionCards,
  buildTransitionDonutStyle,
  deriveBlockRiskStatus,
  fmt,
  pct,
} from "@/utils/enrolmentDerived";
import "./EnrolmentRetentionRiskTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const PAGE_SIZE = 10;

const TRANSITION_ICONS = {
  "primary-upper": Users,
  "upper-secondary": BookOpen,
  "secondary-hs": AlertTriangle,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const formatAxisK = (value) => `${Math.round(value / 1000)}K`;

export default function EnrolmentRetentionRiskTab({
  overview = {},
  retentionData = [],
  blockData = [],
  smallSchools = [],
  largeSchools = [],
  sizeDistribution = [],
  classData = [],
  onViewInsights,
}) {
  const [page, setPage] = useState(1);

  const buildLocalMarkdown = useCallback(
    () =>
      buildRetentionInsightsMarkdown(overview, blockData, retentionData, classData),
    [overview, blockData, retentionData, classData]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        ret: overview.secondary_hs_retention,
        schools: overview.total_schools,
        blocks: blockData.length,
      }),
    [overview.secondary_hs_retention, overview.total_schools, blockData.length]
  );

  const { markdown: aiMarkdown, loading: insightsLoading } = useMistralFirstInsights({
    kind: "enrolment-retention",
    title: "Retention Risk Insights",
    buildLocalMarkdown,
    context: { overview, blockData, retentionData, classData, smallSchools, largeSchools, sizeDistribution },
    enabled: Boolean(overview?.total_schools),
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const transitionCards = useMemo(() => buildTransitionCards(overview), [overview]);
  const localAlert = useMemo(() => buildRetentionAlertCopy(overview), [overview]);
  const alert = useMemo(() => {
    if (insightsLoading) return { ...localAlert, show: false };
    const rootBullets = parseInsightBullets(buckets.root);
    const insightBullets = parseInsightBullets(buckets.insights);
    const bullets = rootBullets.length ? rootBullets : insightBullets;
    return bullets.length ? bulletsToAlertCopy(bullets, localAlert) : localAlert;
  }, [insightsLoading, buckets.root, buckets.insights, localAlert]);
  const chart = useMemo(
    () => buildRetentionComboChart(retentionData, overview),
    [retentionData, overview]
  );
  const kpis = useMemo(
    () => buildRetentionRiskKpis(overview, blockData, sizeDistribution),
    [overview, blockData, sizeDistribution]
  );
  const localHotspots = useMemo(
    () => buildRiskHotspots(overview, blockData, classData, kpis),
    [overview, blockData, classData, kpis]
  );
  const hotspots = useMemo(() => {
    if (insightsLoading) return [];
    const aiHotspots = bulletsToHotspots(parseInsightBullets(buckets.root), localHotspots);
    return aiHotspots?.length ? aiHotspots : localHotspots;
  }, [insightsLoading, buckets.root, localHotspots]);

  const tableRows = useMemo(
    () =>
      blockData.map((block) => ({
        ...block,
        risk: deriveBlockRiskStatus(block.secondary_retention ?? 0),
      })),
    [blockData]
  );

  const totalPages = Math.max(1, Math.ceil(tableRows.length / PAGE_SIZE));
  const pageRows = tableRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(chart.enrolMax * ratio));

  return (
    <div className="enr-rr-tab">
      <section className="enr-rr-retention-grid" aria-label="Transition retention summary">
        {transitionCards.map((card) => {
          const Icon = TRANSITION_ICONS[card.key] || Users;
          return (
            <article
              key={card.key}
              className={`enr-rr-transition-card ${card.status.critical ? "critical" : ""}`.trim()}
            >
              <div className={`enr-rr-icon-box ${card.iconClass}`}>
                <Icon size={22} />
              </div>
              <div
                className="enr-rr-donut-score"
                style={buildTransitionDonutStyle(card.value, card.status.color)}
              >
                <strong>{pct(card.value, 1)}</strong>
              </div>
              <div className="enr-rr-transition-copy">
                <h3>{toTitleCase(card.title)}</h3>
                <p>{card.subtitle}</p>
                <span className={`enr-rr-mini-pill ${card.status.pill}`}>{card.status.label}</span>
                <small>{card.footnote}</small>
              </div>
            </article>
          );
        })}
      </section>

      {alert.show && (
        <section className="enr-rr-alert-banner" aria-label="Critical transition risk alert">
          <div className="enr-rr-icon-box red" style={{ width: 40, height: 40, borderRadius: 13 }}>
            <AlertTriangle size={22} />
          </div>
          <div>
            <b>{alert.title}</b>
            <span>{alert.body}</span>
          </div>
          <button
            type="button"
            className="enr-rr-section-action"
            onClick={() => (onViewInsights ? onViewInsights() : scrollToId("enr-rr-risk-table"))}
          >
            View Recommendations →
          </button>
        </section>
      )}

      <section className="enr-rr-main-analytics" aria-label="Retention and risk diagnostics">
        <article className="enr-rr-panel pad">
          <div className="enr-rr-panel-head">
            <div>
              <h2>Class-Wise Retention & Dropout Trend</h2>
              <p>
                Enrolment base remains strong until secondary stage; dropout risk spikes during Class
                10 → 11 transition.
              </p>
            </div>
            <div className="enr-rr-toolbar">
              <select className="enr-rr-select-mini" aria-label="Class filter" defaultValue="all">
                <option value="all">All Classes</option>
              </select>
              <button type="button" className="enr-rr-page-btn" title="More actions">
                ⋮
              </button>
            </div>
          </div>
          <div className="enr-rr-legend-row">
            <span>
              <i className="enr-rr-legend-dot" />
              Enrolment / Retained Students
            </span>
            <span>
              <i className="enr-rr-legend-line" />
              Dropout %
            </span>
          </div>
          <div className="enr-rr-chart-frame">
            <svg viewBox="0 0 980 360" role="img" aria-label="Combo chart showing enrolment bars and dropout line by class">
              <defs>
                <linearGradient id="enrRrBarGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#3b82f6" />
                  <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
                <filter id="enrRrBarShadow" x="-10%" y="-10%" width="120%" height="130%">
                  <feDropShadow dx="0" dy="8" stdDeviation="7" floodColor="#2563eb" floodOpacity="0.14" />
                </filter>
              </defs>
              <rect x="0" y="0" width="980" height="360" rx="18" fill="rgba(255,255,255,0.42)" />
              <g transform="translate(58 28)">
                <text x="0" y="8" fontSize="12" fontWeight="800" fill="#5d6b84">
                  Students
                </text>
                <text x="842" y="8" fontSize="12" fontWeight="800" fill="#5d6b84">
                  Dropout %
                </text>
                <g stroke="#dbe7f8" strokeDasharray="3 4" strokeWidth="1">
                  <line x1="0" y1="280" x2="840" y2="280" />
                  <line x1="0" y1="214" x2="840" y2="214" />
                  <line x1="0" y1="148" x2="840" y2="148" />
                  <line x1="0" y1="82" x2="840" y2="82" />
                  <line x1="0" y1="16" x2="840" y2="16" />
                </g>
                <g fontSize="11" fill="#8390a8" fontWeight="700">
                  <text x="-30" y="284">
                    0
                  </text>
                  {yTicks.slice(1).map((tick, index) => (
                    <text key={tick} x={index === 0 ? -42 : -48} y={218 - index * 66}>
                      {formatAxisK(tick)}
                    </text>
                  ))}
                  <text x="852" y="284">
                    0%
                  </text>
                  <text x="852" y="218">
                    5%
                  </text>
                  <text x="852" y="152">
                    10%
                  </text>
                  <text x="852" y="86">
                    15%
                  </text>
                  <text x="852" y="20">
                    20%
                  </text>
                </g>
                {chart.riskZone && (
                  <>
                    <rect
                      x={chart.riskZone.x}
                      y="16"
                      width={chart.riskZone.width}
                      height="264"
                      fill="#fee2e2"
                      opacity="0.55"
                      stroke="#ef4444"
                      strokeDasharray="4 4"
                    />
                    <text x={chart.riskZone.x + 15} y="8" fill="#ef4444" fontSize="12" fontWeight="900">
                      High Risk Zone
                    </text>
                  </>
                )}
                <g filter="url(#enrRrBarShadow)" fill="url(#enrRrBarGrad)">
                  {chart.points.map((point) => (
                    <rect
                      key={point.label}
                      x={point.x}
                      y={point.barY}
                      width="42"
                      height={point.barH}
                      rx="6"
                    />
                  ))}
                </g>
                {chart.linePath && (
                  <polyline
                    points={chart.points.map((point) => `${point.cx},${point.lineY}`).join(" ")}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                <g fill="#fff" stroke="#ef4444" strokeWidth="2">
                  {chart.points.map((point) => (
                    <circle
                      key={`${point.label}-dot`}
                      cx={point.cx}
                      cy={point.lineY}
                      r={point.isRisk ? 5 : 4}
                    />
                  ))}
                </g>
                <g fill="#5d6b84" fontSize="10" fontWeight="800" textAnchor="middle">
                  {chart.points.map((point) => (
                    <text
                      key={`${point.label}-label`}
                      x={point.cx}
                      y="306"
                      fill={point.isRisk ? "#ef4444" : "#5d6b84"}
                    >
                      {point.shortLabel}
                    </text>
                  ))}
                  <text x="430" y="338" fontSize="12">
                    Class
                  </text>
                </g>
              </g>
            </svg>
          </div>
        </article>

        <article className="enr-rr-panel pad">
          <div className="enr-rr-panel-head">
            <div>
              <h3>Risk Hotspots</h3>
              <p>Top transition and retention concerns</p>
            </div>
            <span className="enr-rr-tag blue">Live</span>
          </div>
          <div className="enr-rr-risk-list">
            {hotspots.map((item) => (
              <div key={item.rank} className="enr-rr-risk-item">
                <div className={`enr-rr-risk-rank ${item.rankClass}`}>{item.rank}</div>
                <div>
                  <b>{item.title}</b>
                  <span>{item.body}</span>
                </div>
                <span className={`enr-rr-tag ${item.tag}`}>{item.tagLabel}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="enr-rr-list-link"
            onClick={() => (onViewInsights ? onViewInsights() : scrollToId("enr-rr-risk-table"))}
          >
            View All Risk Insights →
          </button>
        </article>
      </section>

      <section className="enr-rr-lower-grid" aria-label="School risk watchlists">
        <article className="enr-rr-list-card">
          <div className="enr-rr-list-title">
            <div className="enr-rr-icon-box orange" style={{ width: 38, height: 38, borderRadius: 12 }}>
              <AlertTriangle size={22} />
            </div>
            <div>
              <h3>Small Schools (&lt;100 Students) – Watchlist</h3>
              <p>Viability risk • Consider merge/upgrade</p>
            </div>
          </div>
          <div className="enr-rr-school-list">
            {smallSchools.slice(0, 5).map((school) => (
              <div key={school.udise_code} className="enr-rr-school-row warn">
                <SchoolLink udiseCode={school.udise_code}>{school.school_name}</SchoolLink>
                <span className="enr-rr-count-badge warn">{fmt(school.grand_total)}</span>
              </div>
            ))}
          </div>
          <button type="button" className="enr-rr-list-link">
            View All {fmt(kpis.smallCount)} Small Schools →
          </button>
        </article>

        <article className="enr-rr-list-card">
          <div className="enr-rr-list-title">
            <div className="enr-rr-icon-box" style={{ width: 38, height: 38, borderRadius: 12 }}>
              <Building2 size={22} />
            </div>
            <div>
              <h3>Large Schools (&gt;1000 Students) – Capacity Risk</h3>
              <p>Infrastructure capacity check needed</p>
            </div>
          </div>
          <div className="enr-rr-school-list">
            {largeSchools.slice(0, 5).map((school) => (
              <div key={school.udise_code} className="enr-rr-school-row info">
                <SchoolLink udiseCode={school.udise_code}>{school.school_name}</SchoolLink>
                <span className="enr-rr-count-badge">{fmt(school.grand_total)}</span>
              </div>
            ))}
          </div>
          <button type="button" className="enr-rr-list-link">
            View All {fmt(kpis.largeCount)} Large Schools →
          </button>
        </article>

        <div className="enr-rr-mini-kpi-grid" aria-label="Risk KPI summary">
          <article className="enr-rr-mini-kpi red">
            <div>
              <div className="enr-rr-icon-box red" style={{ margin: "0 auto", width: 40, height: 40, borderRadius: 13 }}>
                <ShieldAlert size={22} />
              </div>
              <strong>{fmt(kpis.atRiskSchools)}</strong>
              <b>At-risk Schools</b>
              <span>{kpis.atRiskPct} of total schools</span>
            </div>
          </article>
          <article className="enr-rr-mini-kpi amber">
            <div>
              <div className="enr-rr-icon-box orange" style={{ margin: "0 auto", width: 40, height: 40, borderRadius: 13 }}>
                <Calendar size={22} />
              </div>
              <strong>{fmt(kpis.lowRetentionBlocks)}</strong>
              <b>Low Retention Blocks</b>
              <span>Below 85% target</span>
            </div>
          </article>
          <article className="enr-rr-mini-kpi blue">
            <div>
              <div className="enr-rr-icon-box" style={{ margin: "0 auto", width: 40, height: 40, borderRadius: 13 }}>
                <UserRound size={22} />
              </div>
              <strong>{fmt(kpis.schoolsNeedingIntervention)}</strong>
              <b>Schools Needing Intervention</b>
              <span>Priority action required</span>
            </div>
          </article>
        </div>
      </section>

      <section className="enr-rr-panel enr-rr-table-card" id="enr-rr-risk-table" aria-label="Block-wise retention summary">
        <div className="enr-rr-table-head">
          <div>
            <h3>Block-Wise Retention Summary</h3>
            <p>
              Prioritize high-risk blocks for transition support, counselling, and higher-secondary
              capacity review.
            </p>
          </div>
          <button type="button" className="enr-rr-section-action">
            View Full Block Report →
          </button>
        </div>
        <table className="enr-rr-table">
          <thead>
            <tr>
              <th>Block</th>
              <th>Schools</th>
              <th>Enrolment</th>
              <th>GPI</th>
              <th>Avg Size</th>
              <th>Sec → HS Retention</th>
              <th>Risk Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((block) => (
              <tr key={block.block_code || block.block_name}>
                <td>
                  <BlockLink blockCode={block.block_code}>{block.block_name}</BlockLink>
                </td>
                <td>{fmt(block.total_schools)}</td>
                <td>{fmt(block.grand_total)}</td>
                <td>
                  <span className="enr-rr-metric-pill">{block.gpi}</span>
                </td>
                <td>{fmt(block.avg_school_size)}</td>
                <td>{pct(block.secondary_retention ?? 0, 1)}</td>
                <td>
                  <span className={`enr-rr-tag ${block.risk.tag}`}>{block.risk.label}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="enr-rr-table-footer">
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, tableRows.length)} of{" "}
            {tableRows.length} blocks
          </span>
          <div className="enr-rr-pager">
            <button
              type="button"
              className="enr-rr-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={`enr-rr-page-btn ${page === pageNumber ? "active" : ""}`.trim()}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              className="enr-rr-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              ›
            </button>
          </div>
          <button type="button" className="enr-rr-list-link" style={{ margin: 0 }}>
            Export table →
          </button>
        </div>
      </section>
    </div>
  );
}
